import React, { useState } from 'react';
import {
  JiraConnectionConfig,
  JiraConnectionService,
} from '../../services/jira/jiraConnectionService';

const ATLASSIAN_API_TOKENS_URL = 'https://id.atlassian.com/manage-profile/security/api-tokens';

export interface JiraConnectionProps {
  /** compact: tighter spacing for onboarding panel */
  variant?: 'default' | 'compact';
  /** Called after credentials are saved successfully */
  onCredentialsSaved?: () => void;
  /** Omit the API token help block (e.g. onboarding keeps copy shorter) */
  hideHelp?: boolean;
}

export const JiraConnection: React.FC<JiraConnectionProps> = ({
  variant = 'default',
  onCredentialsSaved,
  hideHelp = false,
}) => {
  const [config, setConfig] = useState<JiraConnectionConfig>({
    email: '',
    token: '',
    domain: '',
    project: '',
  });
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<string>('');

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await JiraConnectionService.saveCredentials(config);
      setIsConnected(true);
      setTestResult('✅ Configuration saved successfully!');
      onCredentialsSaved?.();
    } catch (error) {
      console.error('Error saving Jira configuration:', error);
      setTestResult(
        `❌ Failed to save configuration: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleTest = async () => {
    setIsLoading(true);
    setTestResult('');
    try {
      const result = await JiraConnectionService.testConnection(config);

      if (result.connected) {
        setIsConnected(true);
        let message = `✅ ${result.message}`;
        if (result.user) {
          message += `\n👤 User: ${result.user.displayName} (${result.user.emailAddress})`;
        }
        if (result.project) {
          message += `\n📁 Project: ${result.project.name} (${result.project.key})`;
        }
        if (result.hasProjectAccess) {
          message += `\n✅ Project access confirmed`;
        }
        setTestResult(message);
      } else {
        setIsConnected(false);
        setTestResult(`❌ ${result.error || result.message || 'Connection failed'}`);
      }
    } catch (error) {
      setIsConnected(false);
      setTestResult(
        `❌ Connection test failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const compact = variant === 'compact';

  return (
    <div className={`jira-connection${compact ? ' jira-connection--compact' : ''}`}>
      <div className="connection-header">
        <h3>
          Jira Configuration
          <span
            style={{
              marginLeft: '0.5rem',
              fontSize: '0.75rem',
              color: '#6b7280',
              fontWeight: 'normal',
            }}
            title="Configure your Jira instance connection settings for data extraction"
          >
            ℹ️
          </span>
        </h3>
        <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? '✅ Connected' : '❌ Disconnected'}
        </div>
      </div>

      <div className="connection-form">
        <div id="settings-org-jira-anchor" className="form-group">
          <label htmlFor="domain">Jira Domain</label>
          <p id="domain-hint" className="jira-field-hint">
            Hostname only — e.g.{' '}
            <code className="jira-field-hint__code">your-company.atlassian.net</code>. Omit{' '}
            <code className="jira-field-hint__code">https://</code> and paths.
          </p>
          <input
            type="text"
            id="domain"
            value={config.domain || ''}
            onChange={(e) => setConfig({ ...config, domain: e.target.value })}
            placeholder="your-company.atlassian.net"
            aria-describedby="domain-hint"
            autoComplete="off"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <p id="email-hint" className="jira-field-hint">
            Same email you use to sign in to Atlassian / Jira Cloud.
          </p>
          <input
            type="email"
            id="email"
            value={config.email}
            onChange={(e) => setConfig({ ...config, email: e.target.value })}
            placeholder="your-email@company.com"
            aria-describedby="email-hint"
            autoComplete="email"
          />
        </div>

        <div id="settings-product-jira-anchor" className="form-group">
          <label htmlFor="project">Project Key</label>
          <p id="project-hint" className="jira-field-hint">
            Short prefix from issue keys — e.g. <code className="jira-field-hint__code">TNG3</code>{' '}
            from <code className="jira-field-hint__code">TNG3-42</code>. In Jira: Project settings →
            Details.
          </p>
          <input
            type="text"
            id="project"
            value={config.project || ''}
            onChange={(e) => setConfig({ ...config, project: e.target.value.toUpperCase() })}
            placeholder="e.g. PROJ"
            aria-describedby="project-hint"
            autoComplete="off"
          />
        </div>

        <div className="form-group">
          <label htmlFor="apiToken">API Token</label>
          <p id="api-token-hint" className="jira-field-hint">
            <a href={ATLASSIAN_API_TOKENS_URL} target="_blank" rel="noopener noreferrer">
              Create an API token
            </a>{' '}
            under Atlassian account → Security. This is not your Jira login password.
          </p>
          <input
            type="password"
            id="apiToken"
            value={config.token}
            onChange={(e) => setConfig({ ...config, token: e.target.value })}
            placeholder="Your Jira API token"
            aria-describedby="api-token-hint"
            autoComplete="off"
          />
        </div>

        {testResult && (
          <div
            className={`test-result${testResult.includes('✅') ? ' test-result--ok' : ' test-result--err'}`}
          >
            {testResult}
          </div>
        )}

        <div id="settings-test-anchor" className="form-actions">
          <button
            onClick={handleTest}
            disabled={isLoading || !config.email || !config.token || !config.domain}
            className="btn btn-outline"
            title="Test the connection with current settings"
          >
            {isLoading ? '⏳ Testing...' : '🔍 Test Connection'}
          </button>

          <button
            onClick={handleSave}
            disabled={isLoading || !config.email || !config.token || !config.domain}
            className="btn btn-primary"
            title="Save the configuration"
          >
            {isLoading ? '⏳ Saving...' : '💾 Save Configuration'}
          </button>
        </div>
      </div>

      {!hideHelp ? (
        <div className="connection-help">
          <h4>How to get your API token:</h4>
          <ol>
            <li>
              Go to{' '}
              <a href={ATLASSIAN_API_TOKENS_URL} target="_blank" rel="noopener noreferrer">
                Atlassian Account Settings
              </a>
            </li>
            <li>Click "Create API token"</li>
            <li>Give it a label (e.g., "Relix")</li>
            <li>Copy the generated token</li>
            <li>Paste it in the API Token field above</li>
          </ol>
        </div>
      ) : null}
    </div>
  );
};
